/** Nederlandse opmaak, hardcoded. Geen i18n-framework, zoals afgesproken. */

const dateFormatter = new Intl.DateTimeFormat('nl-NL', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

const shortDateFormatter = new Intl.DateTimeFormat('nl-NL', {
  day: 'numeric',
  month: 'short',
})

const timeFormatter = new Intl.DateTimeFormat('nl-NL', {
  hour: '2-digit',
  minute: '2-digit',
})

/** "22 augustus 2026" */
export function formatEventDate(iso: string): string {
  return dateFormatter.format(new Date(iso))
}

/** "21:04" */
export function formatTime(iso: string): string {
  return timeFormatter.format(new Date(iso))
}

/**
 * "vanavond", "vandaag", "gisteren" of "12 aug".
 *
 * "vanavond" vanaf zes uur, want dat is wanneer een trouwfeest begint en het
 * is de tekst die in het ontwerp staat.
 */
export function formatDayLabel(iso: string, now: Date = new Date()): string {
  const moment = new Date(iso)

  const days = daysBetween(moment, now)

  if (days === 0) {
    return moment.getHours() >= 18 ? 'vanavond' : 'vandaag'
  }

  if (days === 1) {
    return 'gisteren'
  }

  return shortDateFormatter.format(moment)
}

/** "21:04 · vanavond" */
export function formatMoment(iso: string, now?: Date): string {
  return `${formatTime(iso)} · ${formatDayLabel(iso, now)}`
}

function daysBetween(moment: Date, now: Date): number {
  const a = new Date(moment.getFullYear(), moment.getMonth(), moment.getDate())
  const b = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  return Math.round((b.getTime() - a.getTime()) / 86_400_000)
}

/** "0:47" — video's op een feest halen zelden een uur. */
export function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) {
    return null
  }

  const total = Math.max(0, Math.round(seconds))
  const minutes = Math.floor(total / 60)

  return `${minutes}:${String(total % 60).padStart(2, '0')}`
}

/** "3,2 MB" — Nederlandse komma, één decimaal zoals in het ontwerp. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`
  }

  const units = ['kB', 'MB', 'GB']
  let value = bytes / 1024
  let unit = 0

  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }

  const decimals = value >= 100 ? 0 : 1

  return `${value.toFixed(decimals).replace('.', ',')} ${units[unit]}`
}

/** "nog ± 40 sec", "nog ± 3 min". Null als er niets zinnigs te zeggen is. */
export function formatEta(seconds: number | null): string | null {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0) {
    return null
  }

  if (seconds < 90) {
    return `nog ± ${Math.max(5, Math.round(seconds / 5) * 5)} sec`
  }

  return `nog ± ${Math.round(seconds / 60)} min`
}

/**
 * "Lotte & Sander" wordt "L&S", "Tante Marleen" wordt "TM".
 *
 * Splitst op de ampersand als die er staat, zodat het monogram van het paar de
 * vorm houdt die het ontwerp laat zien.
 */
export function initials(name: string): string {
  const trimmed = name.trim()

  if (!trimmed) {
    return '?'
  }

  if (trimmed.includes('&')) {
    return trimmed
      .split('&')
      .map((part) => firstLetter(part))
      .filter(Boolean)
      .slice(0, 2)
      .join('&')
  }

  const letters = trimmed
    .split(/\s+/)
    .map((part) => firstLetter(part))
    .filter(Boolean)

  return (letters.length > 1 ? letters[0] + letters[letters.length - 1] : letters[0]) || '?'
}

function firstLetter(part: string): string {
  return [...part.trim()].at(0)?.toLocaleUpperCase('nl-NL') ?? ''
}

/** "1 foto" / "12 foto's", zonder overal een ternary in de JSX. */
export function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`
}

/**
 * De grenzen van een datumveld.
 *
 * Zonder deze accepteert een `<input type="date">` een jaartal tot 275760: typ
 * je 2024 en corrigeer je naar 2026, dan staat er 242026 en meldt de browser
 * dat het geldig is. Met een boven- en ondergrens weigert hij dat, en de
 * onderstaande controle houdt het ook uit onze eigen state.
 */
export const dateBounds = {
  min: `${new Date().getFullYear() - 5}-01-01`,
  max: `${new Date().getFullYear() + 10}-12-31`,
}

/** Of een ingetypte datum binnen die grenzen valt. Leeg mag altijd. */
export function isSaneDate(value: string): boolean {
  return value === '' || (value >= dateBounds.min && value <= dateBounds.max)
}
